/**
 * ADR-0263 §8 — client for `POST /v1/account/agency-request`.
 *
 * Mirrors the discriminated-union pattern in
 * `account-buyer-detail-retention.ts`: the caller branches on
 * `result.kind` instead of catching exceptions. A 202 response is
 * `ok`; the dashboard then clears the `sill_plan_intent` localStorage
 * key. 409 / 4xx also clear (the request is terminally resolved on
 * the server); only `network_error` keeps the intent for a one-shot
 * retry on the next load.
 */

import { ApiError, type HttpClient } from './http-client.js';

export type AgencyRequestResult =
  | { readonly kind: 'ok'; readonly status: 'pending' }
  | { readonly kind: 'forbidden'; readonly reason: string | undefined }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'bad_request'; readonly reason: string | undefined }
  | { readonly kind: 'rate_limited' }
  | { readonly kind: 'network_error' };

export interface AgencyRequestBody {
  readonly plan_slug: string;
  readonly interval: 'monthly' | 'yearly';
}

export async function postAgencyRequest(
  http: HttpClient,
  body: AgencyRequestBody,
): Promise<AgencyRequestResult> {
  try {
    const res = await http.request<{ ok: boolean; status: 'pending' }>({
      method: 'POST',
      path: '/v1/account/agency-request',
      body,
    });
    if (res !== undefined && res.ok === true) {
      return { kind: 'ok', status: 'pending' };
    }
    return { kind: 'network_error' };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') {
        return { kind: 'forbidden', reason: err.server_reason };
      }
      if (err.kind === 'not_found') {
        return { kind: 'not_found' };
      }
      if (err.kind === 'bad_request') {
        return { kind: 'bad_request', reason: err.server_reason };
      }
      if (err.kind === 'rate_limited') {
        return { kind: 'rate_limited' };
      }
    }
    return { kind: 'network_error' };
  }
}
